import React, { Fragment, useState, useCallback, useEffect } from 'react';
import { DateTimePicker } from '@material-ui/pickers';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import { cleanupFormValues, time } from '~/utils';
import { TextInput, AutocompleteArrayTextInput, SelectInput, AddFilterAction } from '~/components';
import { selectors, filterChange, cleanupFilters } from '~/ducks/virtualizedList';
import { Moment } from '~/types';
import { resource } from '../index';
import { EventFilters } from '~/types/Event';
import { useTranslate } from 'react-admin';
import BackgroundTaskExport from '~/components/BackgroundTaskExport';
import { useApi } from '~/hooks';
import { getTaskTypes } from '~/api/events';
import { isEmpty } from 'lodash';
import { IconButton } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/RemoveCircleOutline';
import cx from 'classnames';
import { useListFilterStyles } from '~/hooks/useListFilterStyles';
import { hasCurrentLocale12HourFormat } from '~/utils/moment';

const TASK_STATES = [
  'INITIAL',
  'IN_PROGRESS',
  'RETRY_IS_SCHEDULED',
  'DONE',
  'FAILED',
  'CANCELLED',
  'ERROR',
  'EXPIRED',
  'TERMINATED',
];

interface Props {
  cleanFilters?: boolean;
}

const Filters = (props: Props) => {
  const { cleanFilters = true } = props;
  const dispatch = useDispatch();
  const classes = useStyles();
  const listFilterClasses = useListFilterStyles();
  const translate = useTranslate();
  const t = (key: string): string => translate(`resources.events.list.filters.${key}`);

  const filters = useSelector(selectors.getFilters<EventFilters>(resource));
  const [localValues, setLocalValues] = useState(filters);
  const [addedFilters, setAddedFilters] = useState<string[]>([]);

  useEffect(() => {
    setLocalValues({ ...filters });
  }, [filters]);

  useEffect(() => {
    const activeKeys = Object.keys(filters).filter((k) => !!(filters as any)[k]);
    setAddedFilters((prev) => {
      const merged = new Set([...prev, ...activeKeys]);
      return Array.from(merged);
    });
  }, [filters]);

  const dispatchFilterChange = (name: string, value: string | Moment | string[] | undefined) => {
    dispatch(filterChange({ resource, filters: { [name]: value || null } }));
  };

  const debouncedDispatchFilterChange = useCallback(debounce(dispatchFilterChange, 1000), []);
  const debouncedHandleInput = (e: any) => {
    const { name, value } = e.target;
    setLocalValues({
      ...localValues,
      [name]: value,
    });

    debouncedDispatchFilterChange(name, value);
  };

  const [{ data: taskTypes, loading: taskTypesLoading }] = useApi<string[]>(() => {
    return getTaskTypes();
  });

  const handleDateTimeChange = (data: Moment, inputType: string) => {
    setLocalValues({
      ...localValues,
      [inputType]: data,
    });
    dispatchFilterChange(inputType, data);
  };

  const taskStateOptions = TASK_STATES.map((state) => {
    return {
      value: state,
      text: translate(`eventProcessingState.${state}`),
    };
  });

  const filtersAreEmpty = isEmpty(filters);
  const withFilters = !!window.location.search;
  useEffect(() => {
    if (filtersAreEmpty || (cleanFilters && !withFilters)) {
      dispatch(cleanupFilters({ resource }));
      dispatch(
        filterChange({
          resource,
          filters: {
            timestampFrom: time().subtract(1, 'months'),
            timestampTo: time(),
          },
        })
      );
      setAddedFilters([]);
    }
  }, [dispatch, cleanFilters, filtersAreEmpty, withFilters]);

  const hideFilter = (filter: string) => {
    const inputName = filter;
    dispatchFilterChange(inputName, '');
    setAddedFilters(addedFilters.filter((f) => f !== filter));
  };

  const allFilters = ['tags', 'topic', 'actorId', 'objectId', 'taskType', 'taskState'];

  const renderAddedFilter = (key: string) => {
    switch (key) {
      case 'topic':
        return (
          <TextInput
            name='topic'
            onChange={debouncedHandleInput}
            value={localValues.topic ?? ''}
            label={t('topic')}
            disableHelperText
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'objectId':
        return (
          <TextInput
            name='objectId'
            onChange={debouncedHandleInput}
            value={localValues.objectId ?? ''}
            label={t('objectOrReference')}
            disableHelperText
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'tags':
        return (
          <AutocompleteArrayTextInput
            name='tags'
            label={t('tags')}
            value={localValues.tags}
            regularHelperText={t('tagsHelperText')}
            onChange={(_e: any, value: string[], reason: string) => {
              switch (reason) {
                case 'create-option':
                case 'remove-option':
                  debouncedHandleInput({ target: { name: 'tags', value } });
                  break;
                case 'clear':
                  debouncedHandleInput({ target: { name: 'tags', value: [] } });
                  break;
                default:
                  break;
              }
            }}
            className={cx(
              listFilterClasses.formControl,
              listFilterClasses.formControlAdd,
              listFilterClasses.formControlAddLong
            )}
          />
        );
      case 'actorId':
        return (
          <TextInput
            name='actorId'
            onChange={debouncedHandleInput}
            value={localValues.actorId ?? ''}
            label={t('actor')}
            disableHelperText
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'taskType':
        return (
          <SelectInput<string | undefined>
            label={t('taskType')}
            name='taskType'
            value={localValues.taskType}
            options={
              taskTypes?.map((taskType) => {
                return {
                  value: taskType,
                  text: taskType,
                };
              }) || []
            }
            onChange={debouncedHandleInput}
            disabled={taskTypesLoading}
            allowEmpty
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'taskState':
        return (
          <SelectInput<string | undefined>
            label={t('taskState')}
            name='taskState'
            value={localValues.taskState}
            options={taskStateOptions}
            onChange={debouncedHandleInput}
            allowEmpty
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );

      default:
        return null;
    }
  };

  const translateFilter = (key: string) => {
    switch (key) {
      case 'actorId':
        return t('actor');
      case 'objectId':
        return t('object');
      default:
        return t(key);
    }
  };

  // Render added filters before the boolean switch
  // to ensure the switch is always at the end of the filter list
  // and to avoid missmathced filter widths
  const renderAddedFilters = () =>
    addedFilters.map((filter) => {
      const component = renderAddedFilter(filter);
      if (component) {
        return (
          <div className={listFilterClasses.flexContainer} key={filter}>
            {component}
            <IconButton
              aria-label='delete'
              className={listFilterClasses.deleteIcon}
              onClick={() => hideFilter(filter)}
            >
              <DeleteIcon />
            </IconButton>
          </div>
        );
      } else {
        return null;
      }
    });

  return (
    <Fragment>
      {filters && (
        <div className={listFilterClasses.container}>
          <div className={classes.leftSide}>
            <div className={listFilterClasses.inputsGroup}>
              <DateTimePicker
                className={cx(
                  listFilterClasses.formControl,
                  listFilterClasses.formControlLong,
                  listFilterClasses.datePickers
                )}
                format='L - LT'
                ampm={hasCurrentLocale12HourFormat()}
                margin='normal'
                label={t('timestampFrom')}
                value={localValues.timestampFrom || null}
                onChange={(date) => handleDateTimeChange(date as Moment, 'timestampFrom')}
                maxDate={localValues.timestampTo || time().format()}
              />
              <DateTimePicker
                className={cx(
                  listFilterClasses.formControl,
                  listFilterClasses.formControlLong,
                  listFilterClasses.datePickers
                )}
                format='L - LT'
                ampm={hasCurrentLocale12HourFormat()}
                margin='normal'
                label={t('timestampTo')}
                value={localValues.timestampTo || null}
                onChange={(date) => handleDateTimeChange(date as Moment, 'timestampTo')}
                maxDate={time().format()}
                minDate={localValues.timestampFrom || undefined}
              />
              {renderAddedFilters()}
            </div>
          </div>
          <div className={classes.rightSide}>
            <AddFilterAction
              filters={allFilters
                .filter((f) => !addedFilters.includes(f))
                .sort()
                .map((filterKey) => ({
                  key: filterKey,
                  label: translateFilter(filterKey),
                }))}
              onFilterSelected={(filter: string) => {
                setAddedFilters(addedFilters.concat([filter]));
              }}
            />
            <BackgroundTaskExport type='EVENT_EXPORT' queryArguments={handleFilters(localValues)} />
          </div>
        </div>
      )}
    </Fragment>
  );
};

const handleFilters = (filters: any) => {
  if (!filters) return {};
  const { timestampFrom, timestampTo, ...rest } = filters;
  return cleanupFormValues({
    timestampFrom: handleDate(timestampFrom),
    timestampTo: handleDate(timestampTo),
    ...rest,
  });
};

const handleDate = (date?: Moment | null) => {
  if (!date) return undefined;
  return date.toISOString();
};

const useStyles = makeStyles(() => {
  return {
    leftSide: {
      maxWidth: 'calc(100% - 250px)',
    },
    // Can change width to fit export or other buttons on the right side
    rightSide: { minWidth: 250, textAlign: 'right' },
  };
});

export default Filters;
